/* eslint-disable */
import React, { memo, useEffect, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import {
  enhancePdfPreviewCards,
  isBaiboonMediaUrl,
  isHtmlAnswer,
  isPdfUrl,
  sanitizeAnswerHtml,
} from 'src/sections/baiboonAdmin/baiboonAnswer';

const answerSx = {
  fontSize: '0.875rem',
  lineHeight: 1.55,
  wordBreak: 'break-word',
  '& p': { m: 0, mb: 0.75, '&:last-child': { mb: 0 } },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& a': {
    color: 'inherit',
    fontWeight: 600,
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    maxWidth: '100%',
    display: 'inline-block',
  },
  '& img': {
    display: 'block',
    maxWidth: '100%',
    borderRadius: 1,
    my: 0.75,
    cursor: 'zoom-in',
  },
  '& a.baiboon-file:not(.baiboon-pdf-card)': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.25,
    py: 0.75,
    my: 0.5,
    borderRadius: 1.5,
    bgcolor: alpha('#1a4a7a', 0.1),
    textDecoration: 'none',
    fontWeight: 600,
    cursor: 'pointer',
  },
  '& a.baiboon-pdf-card': {
    display: 'block',
    width: '100%',
    maxWidth: 280,
    my: 0.75,
    borderRadius: 2,
    border: '1px solid',
    borderColor: alpha('#1a4a7a', 0.18),
    bgcolor: '#fff',
    textDecoration: 'none',
    color: 'text.primary',
    overflow: 'hidden',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)',
    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
    '&:hover': {
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
      transform: 'translateY(-1px)',
    },
    '& .baiboon-pdf-card__head': {
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1,
      py: 0.75,
      bgcolor: alpha('#1a4a7a', 0.05),
      borderBottom: '1px solid',
      borderColor: alpha('#1a4a7a', 0.1),
    },
    '& .baiboon-pdf-card__icon': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 30,
      height: 18,
      px: 0.5,
      fontSize: '0.62rem',
      fontWeight: 800,
      letterSpacing: 0.4,
      color: '#fff',
      bgcolor: '#dc2626',
      borderRadius: 0.5,
      flexShrink: 0,
    },
    '& .baiboon-pdf-card__name': {
      fontSize: '0.75rem',
      fontWeight: 600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textDecoration: 'none',
      color: 'text.primary',
    },
    '& .baiboon-pdf-card__preview': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 88,
      bgcolor: '#f1f5f9',
    },
    '& .baiboon-pdf-card__hint': {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#64748b',
    },
  },
};

function BaiboonAnswerView({ text, sx, inverted, onMediaPreview }) {
  const contentRef = useRef(null);

  const html = useMemo(() => {
    if (!text) return '';
    if (!isHtmlAnswer(text)) return '';
    return enhancePdfPreviewCards(sanitizeAnswerHtml(text));
  }, [text]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root || !html || !onMediaPreview) return undefined;

    const onClick = (e) => {
      const img = e.target.closest('img');
      if (img && root.contains(img)) {
        e.preventDefault();
        e.stopPropagation();
        onMediaPreview({
          type: 'image',
          url: img.getAttribute('src'),
          title: img.getAttribute('alt') || 'รูปภาพ',
        });
        return;
      }

      const fileLink = e.target.closest('a');
      if (fileLink && root.contains(fileLink) && isBaiboonMediaUrl(fileLink.getAttribute('href'))) {
        const href = fileLink.getAttribute('href') || '';
        const name = fileLink.getAttribute('data-filename') || 'ไฟล์ PDF';
        if (isPdfUrl(href, name)) {
          e.preventDefault();
          e.stopPropagation();
          onMediaPreview({ type: 'pdf', url: href, title: name });
        }
      }
    };

    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [html, onMediaPreview]);

  if (!text) return null;

  if (!html) {
    return (
      <Typography component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', ...sx }}>
        {text}
      </Typography>
    );
  }

  const hasFile = /baiboon-file|\/api\/baiboon\/media\//i.test(html);
  const hasImage = /<img[\s>]/i.test(html);
  const hasPdfCard = html.includes('baiboon-pdf-card');

  return (
    <Box
      className="baiboon-answer-view"
      sx={{
        ...answerSx,
        overflow: 'hidden',
        maxWidth: '100%',
        '&:has(.bsr-wcard-stack) > div > p:first-of-type, &:has(.bsr-ncard) > div > p:first-of-type': {
          fontSize: '0.8rem',
          color: '#6b7a90',
          mb: 1,
        },
        '& .bsr-ncard p': { m: 0, mb: 0 },
        '& .bsr-ncard__cta a': {
          color: '#fff',
          textDecoration: 'none',
          display: 'block',
          maxWidth: '100%',
        },
        ...(inverted
          ? {
              '& a.baiboon-file:not(.baiboon-pdf-card)': { bgcolor: alpha('#fff', 0.15) },
              '& a.baiboon-pdf-card': {
                bgcolor: alpha('#fff', 0.96),
                borderColor: alpha('#fff', 0.35),
              },
            }
          : {}),
        ...sx,
      }}
    >
      <Box ref={contentRef} dangerouslySetInnerHTML={{ __html: html }} />
      {(hasFile || hasImage) && (
        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, opacity: 0.75 }}>
          <Iconify icon="fa-solid fa-magnifying-glass" width={12} />
          {hasPdfCard ? 'แตะการ์ด PDF หรือรูปเพื่อเปิดดู' : 'แตะรูปหรือไฟล์ PDF เพื่อขยายดู'}
        </Typography>
      )}
    </Box>
  );
}

export default memo(BaiboonAnswerView);
